import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

interface ITEquipmentDialogProps {
  open: boolean;
  onClose: () => void;
  equipment: any;
}

export const ITEquipmentDialog = ({ open, onClose, equipment }: ITEquipmentDialogProps) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    equipment_name: "",
    equipment_type: "",
    supplier_id: "",
    model: "",
    serial_number: "",
    license_key: "",
    purchase_date: "",
    warranty_expiry: "",
    assigned_to: "",
    location: "",
    ip_address: "",
    mac_address: "",
    specifications: "",
    maintenance_notes: "",
    status: "active",
  });

  const { data: suppliers } = useQuery({
    queryKey: ['suppliers-active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  const { data: users } = useQuery({
    queryKey: ['users-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .order('full_name');
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (equipment) {
      setFormData({
        equipment_name: equipment.equipment_name || "",
        equipment_type: equipment.equipment_type || "",
        supplier_id: equipment.supplier_id || "",
        model: equipment.model || "",
        serial_number: equipment.serial_number || "",
        license_key: equipment.license_key || "",
        purchase_date: equipment.purchase_date || "",
        warranty_expiry: equipment.warranty_expiry || "",
        assigned_to: equipment.assigned_to || "",
        location: equipment.location || "",
        ip_address: equipment.ip_address || "",
        mac_address: equipment.mac_address || "",
        specifications: equipment.specifications ? JSON.stringify(equipment.specifications) : "",
        maintenance_notes: equipment.maintenance_notes || "",
        status: equipment.status || "active",
      });
    }
  }, [equipment]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const dataToSave = {
        ...formData,
        supplier_id: formData.supplier_id || null,
        assigned_to: formData.assigned_to || null,
        specifications: formData.specifications ? JSON.parse(formData.specifications) : null,
      };

      if (equipment) {
        const { error } = await supabase
          .from('it_equipment')
          .update(dataToSave)
          .eq('id', equipment.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('it_equipment')
          .insert([{ ...dataToSave, admin_id: user.id }]);
        if (error) throw error;
      }

      toast.success(t('savedSuccessfully'));
      onClose();
    } catch (error) {
      console.error('Error saving IT equipment:', error);
      toast.error(t('errorSavingData'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{equipment ? t('editEquipment') : t('addEquipment')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('equipmentName')} *</Label>
              <Input
                value={formData.equipment_name}
                onChange={(e) => setFormData({ ...formData, equipment_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>{t('equipmentType')} *</Label>
              <Select
                value={formData.equipment_type}
                onValueChange={(v) => setFormData({ ...formData, equipment_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="server">{t('server')}</SelectItem>
                  <SelectItem value="workstation">{t('workstation')}</SelectItem>
                  <SelectItem value="laptop">{t('laptop')}</SelectItem>
                  <SelectItem value="network">{t('network')}</SelectItem>
                  <SelectItem value="software">{t('software')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('supplier')}</Label>
              <Select
                value={formData.supplier_id}
                onValueChange={(v) => setFormData({ ...formData, supplier_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('selectSupplier')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">{t('noSupplier')}</SelectItem>
                  {suppliers?.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('model')}</Label>
              <Input
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('serialNumber')}</Label>
              <Input
                value={formData.serial_number}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('licenseKey')}</Label>
              <Input
                value={formData.license_key}
                onChange={(e) => setFormData({ ...formData, license_key: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('purchaseDate')}</Label>
              <Input
                type="date"
                value={formData.purchase_date}
                onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('warrantyExpiry')}</Label>
              <Input
                type="date"
                value={formData.warranty_expiry}
                onChange={(e) => setFormData({ ...formData, warranty_expiry: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('assignedTo')}</Label>
              <Select
                value={formData.assigned_to}
                onValueChange={(v) => setFormData({ ...formData, assigned_to: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('selectUser')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">-</SelectItem>
                  {users?.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('location')}</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('ipAddress')}</Label>
              <Input
                value={formData.ip_address}
                onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('macAddress')}</Label>
              <Input
                value={formData.mac_address}
                onChange={(e) => setFormData({ ...formData, mac_address: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('status')}</Label>
              <Select
                value={formData.status}
                onValueChange={(v) => setFormData({ ...formData, status: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t('active')}</SelectItem>
                  <SelectItem value="maintenance">{t('maintenance')}</SelectItem>
                  <SelectItem value="retired">{t('retired')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>{t('specifications')}</Label>
              <Textarea
                value={formData.specifications}
                onChange={(e) => setFormData({ ...formData, specifications: e.target.value })}
                placeholder='{"cpu": "Intel i7", "ram": "16GB"}'
              />
            </div>
            <div className="col-span-2">
              <Label>{t('maintenanceNotes')}</Label>
              <Textarea
                value={formData.maintenance_notes}
                onChange={(e) => setFormData({ ...formData, maintenance_notes: e.target.value })}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>{t('cancel')}</Button>
            <Button type="submit">{t('save')}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};